/**
 * FullSyncRunner - 全量分页同步
 *
 * 服务端提供 4 个独立分页接口：
 *   /v1/sdk/sync/friend  — 好友列表（含 user 信息）
 *   /v1/sdk/sync/group   — 群组列表（含 group 信息）
 *   /v1/sdk/sync/label   — 标签列表
 *   /v1/sdk/sync/topic   — 会话列表（含 lastMsg）
 *
 * 策略：sid 差距 > FULL_SYNC_THRESHOLD 时走全量，否则走增量 /sync
 * 全量进行中，增量 /sync 暂停（互斥），全量完成后增量无缝接续
 * 采用 UPSERT 写入，任何时刻中断都有数据可用
 */

import log from "../log";
import time from "../time/time";
import { config_get, config_set } from "../db/t_config";
import { user_batch_upsert, user_query, User } from "../db/t_user";
import {
  user_ref_batch_upsert,
  UserRef,
} from "../db/t_user_ref";
import { group_batch_upsert, Group } from "../db/t_group";
import { group_summary_upsert, GroupSummary } from "../db/t_group_summary";
import { GroupMember, group_member_upsert } from "../db/t_group_member";
import { topic_batch_upsert, Topic, defaultTopic } from "../db/t_topic";
import { msg_batch_add, Msg, defaultMsgDao } from "../db/t_msg";
import { label_insert, Label } from "../db/t_label";
import { CryptEngine } from "../../src/utils/CryptEngine";
import Context from "./context";
import UserController from "./user";
import { MessageType } from "../../src/constants/types";
import { isSystemAcc } from "../../src/screen/Chat/CListItem";

// ==================== 配置常量 ====================

/** 积压任务数超过此值走全量同步（替代旧的 sid 差值判断） */
export const FULL_TASK_THRESHOLD = 50;

/** 每页拉取数量（服务端上限 500） */
const PAGE_SIZE = 500;

/** 全量同步状态持久化 key */
const STATUS_KEY = "full_sync_status";
const EPOCH_KEY = "full_sync_epoch";

/** 全量同步阶段 */
export type FullSyncPhase =
  | "none"
  | "friend"
  | "group"
  | "label"
  | "topic"
  | "done";

/** 持久化的全量同步状态（支持并行阶段跟踪） */
type FullSyncStatus = {
  /** 各阶段是否完成 */
  friendDone: boolean;
  groupDone: boolean;
  labelDone: boolean;
  topicDone: boolean;
  /** 中断时各阶段的 cursor（用于恢复） */
  friendCursor: string; // friend 阶段中断时的 cursor
  groupCursor: string; // group 阶段中断时的 cursor
  labelCursor: string; // label 阶段中断时的 cursor
  topicCursor: string; // topic 阶段中断时的 cursor
  serverSid: number; // 全量同步开始时的服务端 sid
  snapshotCleared?: boolean; // 本轮是否已完成非消息快照清表
  syncEpoch?: number; // 本轮 full_sync 轮次
};

// ==================== 接口响应类型 ====================

type CursorPageRsp<T> = {
  list: T[];
  hasMore: boolean;
  nextCursor: string;
};

/** /v1/sdk/sync/friend 返回的 list 中单条好友关系（不含 user，用户信息在 data.users） */
type FriendItem = {
  id: string;
  friendUid: number;
  scene: string;
  hello: string;
  applyUid: number;
  applyTime: number;
  addedTime: number;
  flag: number;
  utime: number;
  ctime: number;
};

/** /v1/sdk/sync/friend 返回的完整 data 结构 */
type FriendSyncRsp = {
  list: FriendItem[];
  users: User[]; // 用户信息在顶层，不在 list 里
  unreadApply: number; // 好友申请未读数量
  hasMore: boolean;
  nextCursor: string;
};

type GroupItem = {
  group: Group;
  summary?: GroupSummary;
  members?: GroupMember[];
  memberCount?: number; // 群成员数量（服务端 /v1/sdk/sync/group 返回）
};

/** 接口返回的嵌套结构（lastMsg 是子对象） */
type TopicItem = {
  tid: string;
  uid?: number;
  name?: string;
  cid?: string;
  ctime: number;
  type?: string;
  sid?: number;
  hidemid?: string;
  order?: number;
  lvTime?: number;
  lsMid?: string;
  remark?: string;
  mute?: boolean;
  draft?: string;
  unread: number;
  lastMsg?: {
    mid: string;
    tid?: string;
    cid: string | number;
    sender: number;
    stime: number;
    type: string;
    content: string;
    meta?: any;
    etime?: number;
    targetCnt?: number;
    readCnt?: number;
    atCnt?: number;
    atUids?: number[];
    mask?: number;
    visible?: number;
    auditRst?: string;
    encrypt?: number;
    restrict?: boolean;
    rtcId?: number;
    duration?: number;
    senderEmployee?: string;
    senderNick?: string;
    senderName?: string;
  };
};

// ==================== FullSyncRunner ====================

export class FullSyncRunner {
  private readonly ctx: Context;
  private _isRunning: boolean = false;
  private _phase: FullSyncPhase = "none";
  /** 批量分页失败时 UI 提示节流，避免网络抖动连续弹 Toast */
  private lastPagedFailureHintAt = 0;
  private static readonly PAGED_FAILURE_HINT_COOLDOWN_MS = 8000;
  private currentSyncEpoch = 0;

  constructor(ctx: Context) {
    this.ctx = ctx;
  }

  public get isRunning(): boolean {
    return this._isRunning;
  }

  public get phase(): FullSyncPhase {
    return this._phase;
  }

  /**
   * 执行全量同步，全量过程中 /sync 不可请求
   * 返回全量同步获取到的最大 sid（用于更新本地 sid 游标）
   *
   * 并行策略：friend / group / label 三个阶段并行执行
   *           group 完成后才开始 topic（topic 依赖群信息）
   */
  public async run(serverSid: number): Promise<{ sid: number }> {
    if (this._isRunning) {
      log.warn("[FullSync] 已在运行中，跳过");
      return { sid: 0 };
    }

    this._isRunning = true;

    const faAtStart = await config_get(this.ctx.db, "friend_apply");
    // 1. 检查是否有中断恢复的状态
    const savedStatus = await this.loadStatus();
    let status: FullSyncStatus;
    if (
      savedStatus &&
      !(
        savedStatus.friendDone &&
        savedStatus.groupDone &&
        savedStatus.labelDone &&
        savedStatus.topicDone
      )
    ) {
      log.info(
        "[FullSync] 检测到中断恢复，friend完成:",
        savedStatus.friendDone,
        "group完成:",
        savedStatus.groupDone,
        "label完成:",
        savedStatus.labelDone,
        "topic完成:",
        savedStatus.topicDone,
      );
      status = savedStatus;
      this.currentSyncEpoch = status.syncEpoch ?? 0;
    } else {
      status = {
        friendDone: false,
        groupDone: false,
        labelDone: false,
        topicDone: false,
        friendCursor: "",
        groupCursor: "",
        labelCursor: "",
        topicCursor: "",
        serverSid: serverSid,
        snapshotCleared: false,
        syncEpoch: 0,
      };
      this.currentSyncEpoch = 0;
    }

    try {
      this.lastPagedFailureHintAt = 0;
      // 本轮全量仅清表一次；中断恢复时若已清过则跳过
      if (!status.snapshotCleared) {
        this.currentSyncEpoch = await this.bumpSyncEpoch();
        status.syncEpoch = this.currentSyncEpoch;
        await this.clearSnapshotTablesForFreshRun();
        status.snapshotCleared = true;
        await this.saveStatus(status);
      }
      // 2. 发送开始事件
      this.ctx.mq.emit("full_process", {
        full_process: { name: "begin", process: 0, count: 0 },
      });

      // 3. 并行执行 friend / group / label
      const parallelTasks: Promise<void>[] = [];

      if (!status.friendDone) {
        parallelTasks.push(
          this.runPhase("friend", status).then(async (cursor) => {
            if (!this._isRunning) {
              // Destory/SwitchUser 后可能已关库，勿 saveStatus；仅更新内存供日志
              status.friendCursor = cursor;
              return;
            }
            status.friendDone = true;
            status.friendCursor = cursor;
            await this.saveStatus(status);
          }),
        );
      }

      if (!status.groupDone) {
        parallelTasks.push(
          this.runPhase("group", status).then(async (cursor) => {
            if (!this._isRunning) {
              status.groupCursor = cursor;
              return;
            }
            status.groupDone = true;
            status.groupCursor = cursor;
            await this.saveStatus(status);
          }),
        );
      }

      if (!status.labelDone) {
        parallelTasks.push(
          this.runPhase("label", status).then(async (cursor) => {
            if (!this._isRunning) {
              status.labelCursor = cursor;
              return;
            }
            status.labelDone = true;
            status.labelCursor = cursor;
            await this.saveStatus(status);
          }),
        );
      }

      // 等待 friend/group/label 全部完成
      await Promise.all(parallelTasks);

      // 检查是否被中断（勿再读 DB：SwitchUser 可能已关库）
      if (!this._isRunning) {
        log.info("[FullSync] 并行阶段被中断（已跳过读库）");
        return { sid: status.serverSid };
      }

      const faBeforeTopic = await config_get(this.ctx.db, "friend_apply");

      // 4. group 完成后执行 topic（依赖群信息）
      if (!status.topicDone) {
        const topicCursor = await this.runPhase("topic", status);
        if (!this._isRunning) {
          status.topicCursor = topicCursor;
          log.info(
            "[FullSync] topic阶段被中断，cursor=",
            topicCursor,
            "（已跳过写库）",
          );
          return { sid: status.serverSid };
        }
        status.topicDone = true;
        status.topicCursor = topicCursor;
        await this.saveStatus(status);
      }

      // 5. 全量完成后先通知会话列表刷新数据（先有数据再消失骨架）
      this.ctx.mq.emit("topic_refresh", {});
      const faAfterTopicRefresh = await config_get(this.ctx.db, "friend_apply");
      // 6. 数据已就绪，通知 UI 全量完成（消失骨架屏）
      this.ctx.mq.emit("full_process", {
        full_process: { name: "done", process: 100, count: 0 },
      });

      // 7. 清除持久化状态
      await this.clearStatus();

      log.info("[FullSync] 全量同步完成");
    } catch (err) {
      log.error("[FullSync] 全量同步异常中断", err);
      // 异常中断时保存当前进度（Destory 后 _isRunning 为 false，勿对已关库写入）
      if (this._isRunning) {
        try {
          await this.saveStatus(status);
        } catch (e) {
          log.warn("[FullSync] 保存全量进度失败（可能已关库）", e);
        }
      }
      
    } finally {
      this._isRunning = false;
      this._phase = "none";
    }

    return { sid: status.serverSid };
  }

  /** 执行单个阶段的同步，返回最终的 cursor */
  private async runPhase(
    phase: "friend" | "group" | "label" | "topic",
    status: FullSyncStatus,
  ): Promise<string> {
    switch (phase) {
      case "friend":
        return await this.syncFriend(status.friendCursor);
      case "group":
        return await this.syncGroup(status.groupCursor);
      case "label":
        return await this.syncLabel(status.labelCursor);
      case "topic":
        return await this.syncTopic(status.topicCursor);
    }
  }

  /** 中断当前全量同步 */
  public abort() {
    this._isRunning = false;
    log.info("[FullSync] 被中断");
  }

  /**
   * 在 await conn.Call 返回后调用：abort 与在途请求竞态时，循环头里的 _isRunning 检查来不及生效，
   * 若继续写库会触发 OP-SQLite「DB is not open」（SwitchUser 已关库）。
   */
  private stoppedAfterNetworkCall(): boolean {
    if (!this._isRunning) {
      log.info("[FullSync] 请求返回后已中断，跳过本页写库");
      return true;
    }
    return false;
  }

  /**
   * 分页接口失败（非 cursor 400）时通知 UI：网络波动等场景下用户可知原因；本地仍 sleep 重试。
   */
  private emitPagedSyncFailure(
    rst: { code?: number; msg?: string; message?: string } | null | undefined,
    phase: string,
  ) {
    const code = rst?.code ?? -1;
    if (code === 400) {
      return;
    }
    const now = Date.now();
    if (
      now - this.lastPagedFailureHintAt <
      FullSyncRunner.PAGED_FAILURE_HINT_COOLDOWN_MS
    ) {
      return;
    }
    this.lastPagedFailureHintAt = now;
    const hint = (rst as any)?.msg || (rst as any)?.message || "";
    const hintStr = typeof hint === "string" ? hint : "";
    this.ctx.mq.emit("full_process", {
      full_process: {
        name: "error",
        process: 0,
        count: 0,
        phase,
        code,
        hint: hintStr,
        error: hintStr,
      },
    });
  }

  // ==================== 各阶段实现 ====================

  /** 同步好友列表 */
  private async syncFriend(startCursor: string): Promise<string> {
    this._phase = "friend";
    let cursor = startCursor;
    let hasMore = true;
    let total = 0;

    log.info("[FullSync] 开始同步好友, cursor=", cursor);

    while (hasMore && this._isRunning) {
      const rst = await this.ctx.conn.Call("/v1/sdk/sync/friend", {
        cursor: cursor,
        size: PAGE_SIZE,
      });
      if (this.stoppedAfterNetworkCall()) break;
      // log.info("[FullSync] syncFriend 接口返回 ", JSON.stringify(rst));

      if (!rst || rst.code !== 0) {
        log.warn("[FullSync] 同步好友接口失败", rst);
        // cursor 失效（code=400），从空 cursor 重拉
        if (rst && rst.code === 400) {
          log.info("[FullSync] cursor 失效，从空 cursor 重拉");
          cursor = "";
          continue;
        }
        this.emitPagedSyncFailure(rst, "friend");
        await time.sleep(2 * time.SECOND);
        continue;
      }

      const data = rst.data as FriendSyncRsp;
      if (data.list && data.list.length > 0) {
        // 用户信息在 data.users，不在 list 项里
        // 建立 uid → User 映射，方便查找
        const userMap = new Map<number, User>();
        if (data.users && data.users.length > 0) {
          for (const u of data.users) {
            if (u.uid) {
              userMap.set(u.uid, this.fixUserDefault(u));
            }
          }
        }

        const users: User[] = [];
        const refs: UserRef[] = [];
        for (const item of data.list) {
          const uid = item.friendUid;
          const user = userMap.get(uid);
          if (!user) {
            // list 里有 friendUid 但 users 里没有对应用户信息，跳过
            log.warn(
              "[FullSync] friendUid=",
              uid,
              "在 users 中找不到对应信息，跳过",
            );
            continue;
          }
          users.push(user);
          refs.push({
            uid: user.uid,
            isfriend: 1,
            isstaff: 0,
            ischain: 0,
            plat: "",
            appkey: user.appkey || "",
            main_appkey: user.mainAppkey || "",
            lutime: 0,
            lctime: 0,
          });
        }
        // 批量写入 DB
        if (users.length > 0) {
          await user_batch_upsert(this.ctx.db, users);
          await user_ref_batch_upsert(this.ctx.db, refs, ["friend", "chain"]);
        }

        total += data.list.length;
      }

      // 好友申请数：写入 config 且发 MQ（与 sync.ts friend_apply 一致），否则 Redux 要等下次 queryFriendApplyNum 才更新
      if (data.unreadApply !== undefined) {
        const cnt = data.unreadApply;
        const cntStr = String(cnt);
        const r2 = await config_get(this.ctx.db, "friend_apply");
        if (!(r2.data && r2.data.val && r2.data.val === cntStr)) {
          await config_set(this.ctx.db, "friend_apply", cntStr);
          this.ctx.mq.emit_stash("friend_apply", {
            friend_apply: { count: cnt },
          });
        }
      }

      hasMore = data.hasMore || false;
      cursor = data.nextCursor || "";

      // 进度通知（friend 阶段占总进度的 0-25%）
      this.emitProgress(25, total, "friend");
    }

    log.info("[FullSync] 好友同步完成，共", total, "条");
    // 通知通讯录刷新
    this.ctx.mq.emit_stash("contact_refresh", {});
    return cursor;
  }

  /** 同步群组列表 */
  private async syncGroup(startCursor: string): Promise<string> {
    this._phase = "group";
    let cursor = startCursor;
    let hasMore = true;
    let total = 0;

    log.info("[FullSync] 开始同步群组, cursor=", cursor);

    while (hasMore && this._isRunning) {
      const rst = await this.ctx.conn.Call("/v1/sdk/sync/group", {
        cursor: cursor,
        size: PAGE_SIZE,
      });
      if (this.stoppedAfterNetworkCall()) break;

      if (!rst || rst.code !== 0) {
        log.warn("[FullSync] 同步群组接口失败", rst);
        if (rst && rst.code === 400) {
          cursor = "";
          continue;
        }
        this.emitPagedSyncFailure(rst, "group");
        await time.sleep(2 * time.SECOND);
        continue;
      }

      const data = rst.data as CursorPageRsp<GroupItem>;
      if (data.list && data.list.length > 0) {
        // 批量收集（不含群成员，群成员按需加载）
        const groups: Group[] = [];
        for (const item of data.list) {
          if (item.group && item.group.flag !== 2) {
            // 写入 memberCount（服务端新增字段）
            if (item.memberCount !== undefined) {
              item.group.member_count = item.memberCount;
            }
            groups.push(item.group);
            if (item.summary) {
              await group_summary_upsert(this.ctx.db, item.summary);
            }
            // 不写入群成员——群成员数据量大，改为进入聊天时按需加载
          }
        }
        // 批量写入 DB
        if (groups.length > 0) {
          await group_batch_upsert(this.ctx.db, groups);
        }

        total += data.list.length;
      }

      hasMore = data.hasMore || false;
      cursor = data.nextCursor || "";

      // 进度通知（group 阶段占总进度的 25-50%）
      this.emitProgress(50, total, "group");
    }

    log.info("[FullSync] 群组同步完成，共", total, "条");
    return cursor;
  }

  /** 同步标签列表 */
  private async syncLabel(startCursor: string): Promise<string> {
    this._phase = "label";
    let cursor = startCursor;
    let hasMore = true;
    let total = 0;

    log.info("[FullSync] 开始同步标签, cursor=", cursor);

    while (hasMore && this._isRunning) {
      const rst = await this.ctx.conn.Call("/v1/sdk/sync/label", {
        cursor: cursor,
        size: PAGE_SIZE,
      });
      if (this.stoppedAfterNetworkCall()) break;

      if (!rst || rst.code !== 0) {
        log.warn("[FullSync] 同步标签接口失败", rst);
        if (rst && rst.code === 400) {
          cursor = "";
          continue;
        }
        this.emitPagedSyncFailure(rst, "label");
        await time.sleep(2 * time.SECOND);
        continue;
      }

      const data = rst.data as CursorPageRsp<Label>;
      if (data.list && data.list.length > 0) {
        await label_insert(this.ctx.db, data.list);
        total += data.list.length;
      }

      hasMore = data.hasMore || false;
      cursor = data.nextCursor || "";

      // 进度通知（label 阶段占总进度的 50-60%）
      this.emitProgress(60, total, "label");
    }

    log.info("[FullSync] 标签同步完成，共", total, "条");
    return cursor;
  }

  /** 同步会话列表（含 lastMsg） */
  private async syncTopic(startCursor: string): Promise<string> {
    this._phase = "topic";
    let cursor = startCursor;
    let hasMore = true;
    let total = 0;
    // 收集单聊对方的 uid（syncFriend 只同步好友，非好友单聊用户信息需单独获取）
    const singleChatUids: number[] = [];

    log.info("[FullSync] 开始同步会话, cursor=", cursor);

    while (hasMore && this._isRunning) {
      const rst = await this.ctx.conn.Call("/v1/sdk/sync/topic", {
        cursor: cursor,
        size: PAGE_SIZE,
      });

      if (this.stoppedAfterNetworkCall()) break;
      if (!rst || rst.code !== 0) {
        log.warn("[FullSync] 同步会话接口失败", rst);
        if (rst && rst.code === 400) {
          cursor = "";
          continue;
        }
        this.emitPagedSyncFailure(rst, "topic");
        await time.sleep(2 * time.SECOND);
        continue;
      }

      const data = rst.data as CursorPageRsp<TopicItem>;
      if (data.list && data.list.length > 0) {
        // 批量收集
        const topics: Topic[] = [];
        const allMsgs: Msg[] = [];
        // 群聊的 lastMsg sender 拼凑 GroupMember（仅含 nick/name，进入聊天时完整数据会覆盖）
        const partialMembers: GroupMember[] = [];
        for (const item of data.list) {
          const tid = item.tid || "";
          // 单聊 tid 格式: s_{otherUid}_{selfUid}，从 tid 推导 uid 和 type
          // 群聊 tid 格式: g_{群id}，不适用此逻辑
          let topicUid = item.uid || 0;
          let topicType = item.type || "";
          if (topicType === "" && tid.startsWith("s_")) {
            topicType = "single";
            // 从 tid 提取对方 uid: s_{otherUid}_{selfUid}
            const parts = tid.split("_");
            if (parts.length >= 3) {
              const uid1 = parseInt(parts[1]) || 0;
              const uid2 = parseInt(parts[2]) || 0;
              topicUid = uid1 === this.ctx.uid ? uid2 : uid1;
            }
          }
          // 嵌套结构 → Topic 实体
          const topic: Topic = {
            ...defaultTopic,
            tid: tid,
            uid: topicUid,
            name: item.name || "",
            cid: item.cid || "",
            ctime: item.ctime || 0,
            type: topicType,
            sid: item.sid || 0,
            hidemid: item.hidemid || "",
            order: item.order || 0,
            lvTime: item.lvTime || 0,
            lsMid: item.lsMid || "",
            remark: item.remark || "",
            mute: item.mute || false,
            draft: item.draft || "",
            unread: item.unread || 0,
            bridgeEpoch: this.currentSyncEpoch,
            bridgeState: 0,
            bridgeCursor: "",
            bridgePages: 0,
            bridgeUpdatedAt: 0,
            lutime: 0,
            lctime: 0,
          };
          topics.push(topic);
          // 单聊：收集对方 uid，topic 写入后批量获取用户信息
          if (topic.type === "single" && topic.uid > 0) {
            singleChatUids.push(topic.uid);
          }
          // 解密并收集 lastMsg
          if (item.lastMsg && item.lastMsg.mid && item.lastMsg.mid.length > 0) {
            const msgContent = CryptEngine.decode(
              item.lastMsg.encrypt || 0,
              item.lastMsg.content,
              item.lastMsg.cid,
            );
            // 构建 meta，将 senderNick/senderEmployee/senderName 存入
            const meta: any = item.lastMsg.meta || {};
            if (item.lastMsg.senderNick) {
              meta.senderNick = item.lastMsg.senderNick;
            }
            if (item.lastMsg.senderEmployee) {
              meta.senderEmployee = item.lastMsg.senderEmployee;
            }
            if (item.lastMsg.senderName) {
              meta.senderName = item.lastMsg.senderName;
            }
            const msg: Msg = {
              ...defaultMsgDao,
              mid: item.lastMsg.mid,
              tid: item.tid,
              cid:
                typeof item.lastMsg.cid === "number"
                  ? item.lastMsg.cid
                  : parseInt(String(item.lastMsg.cid)) || 0,
              sender: item.lastMsg.sender,
              stime: item.lastMsg.stime,
              type: item.lastMsg.type,
              content: msgContent,
              meta: meta,
              etime: item.lastMsg.etime || 0,
              targetCnt: item.lastMsg.targetCnt || 0,
              readCnt: item.lastMsg.readCnt || 0,
              atCnt: item.lastMsg.atCnt || 0,
              atUids: item.lastMsg.atUids || [],
              mask: item.lastMsg.mask || 0,
              visible: item.lastMsg.visible || 0,
              auditRst: item.lastMsg.auditRst || "",
              encrypt: item.lastMsg.encrypt || 0,
              // restrict: item.lastMsg.restrict || false,
              rtcId: item.lastMsg.rtcId || 0,
              duration: item.lastMsg.duration || 0,
            };
            allMsgs.push(msg);

            // 群聊：用 senderNick/senderName 拼凑一条 GroupMember
            // 后续进入聊天时 GetGroupDetail 拿到完整成员会覆盖（ON CONFLICT DO UPDATE）
            if (topic.type === "group" && item.lastMsg.sender && item.lastMsg.type !== MessageType.SYSTEM) {
              const senderNick = item.lastMsg.senderNick || "";
              const senderName = item.lastMsg.senderName || "";
              if ((senderNick || senderName) && !isSystemAcc(item.lastMsg.sender|| 0)) {
                partialMembers.push({
                  tid: item.tid,
                  uid: item.lastMsg.sender,
                  nick: senderNick,
                  name: senderName,
                  namePY: "",
                  namePYS: "",
                  nickPY: "",
                  nickPYS: "",
                  sex: "",
                  birth: "",
                  privacy: "",
                  telno: "",
                  email: "",
                  appkey: "",
                  mainAppkey: "",
                  avatar: "",
                  remark: "",
                  status: "",
                  lutime: 0,
                  lctime: 0,
                });
              }
            }
          }
        }
        if (topics.length > 0) {
          let sumUnreadApi = 0;
          let cntUnreadTopic = 0;
          const samples: { tid: string; unread: number; lsMid: string }[] = [];
          for (const t of topics) {
            const u = t.unread || 0;
            sumUnreadApi += u;
            if (u > 0) {
              cntUnreadTopic++;
              if (samples.length < 8) {
                samples.push({
                  tid: t.tid,
                  unread: u,
                  lsMid: t.lsMid || "",
                });
              }
            }
          }
        }
        // 批量写入 DB
        if (topics.length > 0) {
          await topic_batch_upsert(this.ctx.db, topics);
        }
        if (allMsgs.length > 0) {
          const batchid = time.toUnixMillSecond(time.now());
          await msg_batch_add(this.ctx.db, this.ctx.uid, batchid, allMsgs);
        }
        // 写入拼凑的群成员（仅 nick/name，进入聊天时补全）
        if (partialMembers.length > 0) {
          await group_member_upsert(this.ctx.db, partialMembers);
        }

        // 通知 UI 本批次数据已写入 DB，UI 可逐条增量更新会话列表
        const batchTids = topics.map((t) => t.tid);
        if (batchTids.length > 0) {
          this.ctx.mq.emit("topic_batch_written", {
            topic_batch_written: { tids: batchTids },
          });
        }

        total += data.list.length;
      }

      hasMore = data.hasMore || false;
      cursor = data.nextCursor || "";

      // 进度通知（topic 阶段占总进度的 60-100%）
      this.emitProgress(100, total, "topic");
    }

    log.info("[FullSync] 会话同步完成，共", total, "条");

    if (!this._isRunning) {
      return cursor;
    }

    // 单聊对方的用户信息可能在 DB 中不存在（syncFriend 只同步好友）
    // 先查本地，只对本地不存在的 uid 调远程，避免重复拉取好友信息
    const uniqueSingleUids = [...new Set(singleChatUids)].filter(
      (uid) => uid !== this.ctx.uid,
    );
    log.info("[FullSync] 单聊对方 uid 列表:", JSON.stringify(uniqueSingleUids));
    if (uniqueSingleUids.length > 0) {
      try {
        // 先查本地已有的用户
        const localRst = await user_query(this.ctx.db, uniqueSingleUids);
        const localUidSet = new Set((localRst.users || []).map((u) => u.uid));
        // 过滤出本地不存在的 uid
        const missingUids = uniqueSingleUids.filter(
          (uid) => !localUidSet.has(uid),
        );
        if (missingUids.length > 0) {
          const BATCH_SIZE = 50;
          for (let i = 0; i < missingUids.length; i += BATCH_SIZE) {
            if (!this._isRunning) break;
            const batch = missingUids.slice(i, i + BATCH_SIZE);
            await UserController.GetRemote(this.ctx, batch);
          }
          log.info(
            "[FullSync] 补全单聊用户信息，本地已有",
            localUidSet.size,
            "人，远程拉取",
            missingUids.length,
            "人",
          );
        } else {
          log.info("[FullSync] 单聊用户信息本地已齐全，无需远程拉取");
        }
      } catch (e) {
        log.warn("[FullSync] 补全单聊用户信息失败", e);
      }
    }

    return cursor;
  }

  // ==================== 辅助方法 ====================

  /** 修复全量接口返回 user 字段不全的问题 */
  private fixUserDefault(user: User): User {
    return Object.assign(
      {
        uid: 0,
        nick: "",
        name: "",
        avatar: "",
        remark: "",
        appkey: "",
        mainAppkey: "",
        telno: "",
        email: "",
        plat: "",
      },
      user,
    );
  }

  /** 发送进度通知 */
  private emitProgress(maxPercent: number, count: number, phase: string) {
    const p = Math.min(maxPercent, Math.floor((count / 100) * maxPercent));
    this.ctx.mq.emit("full_process", {
      full_process: {
        name: "process",
        process: p,
        count: count,
        phase: phase,
      },
    });
  }

  /** 新开一轮全量时，重置非消息快照表；中断恢复不重复执行 */
  private async clearSnapshotTablesForFreshRun(): Promise<void> {
    await this.ctx.db.Execute("delete from t_user_ref", []);
    await this.ctx.db.Execute("delete from t_topic", []);
    await this.ctx.db.Execute("delete from t_group_member", []);
    await this.ctx.db.Execute("delete from t_group_summary", []);
    await this.ctx.db.Execute("delete from t_group", []);
    await this.ctx.db.Execute("delete from t_label", []);
    log.info("[FullSync] 新一轮同步已清空非消息快照表");
  }

  /** 持久化全量同步状态（用于中断恢复） */
  private async saveStatus(status: FullSyncStatus): Promise<void> {
    await config_set(this.ctx.db, STATUS_KEY, JSON.stringify(status));
  }

  /** 全量轮次 +1（用于消息桥接状态失效隔离） */
  private async bumpSyncEpoch(): Promise<number> {
    const r = await config_get(this.ctx.db, EPOCH_KEY);
    const current = Number(r.data?.val || "0");
    const next = Number.isFinite(current) && current > 0 ? current + 1 : 1;
    await config_set(this.ctx.db, EPOCH_KEY, String(next));
    return next;
  }

  /** 加载持久化的全量同步状态 */
  private async loadStatus(): Promise<FullSyncStatus | null> {
    const r = await config_get(this.ctx.db, STATUS_KEY);
    if (r.data && r.data.val) {
      try {
        return JSON.parse(r.data.val) as FullSyncStatus;
      } catch {
        return null;
      }
    }
    return null;
  }

  /** 清除持久化状态 */
  private async clearStatus(): Promise<void> {
    await config_set(this.ctx.db, STATUS_KEY, "");
  }
}
